<template>
  <v-app>
    <AppWrapperView>
      <v-main>
        <v-container class="pa-8" fluid>

          <div class="section_title">{{ $t('setting.label.companyCodeTitle') }}</div>
          <p class="section_desc">{{ $t('setting.message.companyCodeDesc') }}</p>

          <v-alert
            v-if="errorMsg"
            type="error"
            density="compact"
            :text="errorMsg"
            closable
            class="mb-3"
            @click:close="errorMsg = ''"
          />
          <v-alert
            v-if="savedMsg"
            type="success"
            density="compact"
            :text="savedMsg"
            closable
            class="mb-3"
            @click:close="savedMsg = ''"
          />

          <!-- 絞り込みは「検索」を押したとき（または Enter）に反映する。打つたびには絞り込まない
               （他の一覧画面と同じ「クリア」「検索」の形。CR-002 S-25） -->
          <v-row>
            <v-col cols="4">
              <v-text-field
                v-model="filterText"
                density="compact"
                variant="outlined"
                hide-details="auto"
                :label="$t('setting.label.filter')"
                @keydown.enter="onSearch"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" class="text-right">
              <v-btn text @click="clear">{{ $t('common.action.clear') }}</v-btn>
              <v-btn theme="dark" class="ml-4 px-8 bg-grey-darken-3" elevation="2"
                :loading="isSearching" :disabled="editingKey != null" @click="onSearch">{{ $t('common.action.search') }}</v-btn>
            </v-col>
          </v-row>

          <!-- 「行を追加」「一括登録」は画面右上（棚卸日登録と同じ場所）に置く（サーバー修正 S-39、2026-10-27 会議） -->
          <v-row>
            <v-col cols="12" class="text-right">
              <v-btn
                dark
                class="px-6 bg-indigo-darken-1"
                elevation="2"
                :disabled="editingKey != null || isSaving"
                @click="addRow"
              >{{ $t('setting.action.addRow') }}</v-btn>
              <v-btn
                dark
                class="ml-4 px-6 bg-indigo-darken-1"
                elevation="2"
                :disabled="editingKey != null || isSaving"
                @click="bulkDialogVisible = true"
              >{{ $t('setting.action.bulk') }}</v-btn>
            </v-col>
          </v-row>

          <v-table density="compact" class="mt-4 section_table">
            <thead>
              <tr>
                <th class="nowrap">{{ $t('setting.label.table.no') }}</th>
                <th class="nowrap">{{ $t('setting.label.table.companyCode') }}</th>
                <th class="col_text">{{ $t('setting.label.table.companyName') }}</th>
                <th class="col_text">{{ $t('setting.label.table.remarks') }}</th>
                <th class="nowrap">{{ $t('setting.label.table.updatedDatetime') }}</th>
                <th class="nowrap">{{ $t('setting.label.table.action') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in pagedRows" :key="item.row._key" :class="{ editing_row: isEditing(item.row) }">
                <td class="nowrap">{{ item.index + 1 }}</td>

                <!-- 編集中の行だけ入力欄にする（2026-10-23。それ以外は読み取り専用） -->
                <template v-if="isEditing(item.row)">
                  <td class="code_col">
                    <v-text-field
                      density="compact"
                      variant="outlined"
                      hide-details="auto"
                      v-model="draft.companyCode"
                      maxlength="7"
                      placeholder="1234567"
                      class="code_input"
                      :rules="[checkCode]"
                      @input="draft.companyCode = draft.companyCode.replace(/[^0-9]/g, '')"
                    />
                  </td>
                  <td class="col_text">
                    <v-text-field
                      density="compact" variant="outlined" hide-details="auto"
                      v-model="draft.companyName" maxlength="100"
                    />
                  </td>
                  <td class="col_text">
                    <v-text-field
                      density="compact" variant="outlined" hide-details="auto"
                      v-model="draft.remarks" maxlength="255"
                    />
                  </td>
                  <td class="nowrap">{{ formatDatetime(item.row.updatedDatetime) }}</td>
                  <td class="nowrap">
                    <v-btn
                      size="small" variant="outlined" class="mr-2"
                      :disabled="isSaving"
                      @click="cancelEdit"
                    >{{ $t('common.action.cancel') }}</v-btn>
                    <v-btn
                      size="small" variant="flat" color="indigo-darken-1"
                      :loading="isSaving" :disabled="!canFinish"
                      @click="finishEdit"
                    >{{ $t('setting.action.finishEdit') }}</v-btn>
                  </td>
                </template>

                <template v-else>
                  <td class="code_cell nowrap">{{ item.row.companyCode }}</td>
                  <td class="col_text">{{ item.row.companyName || "-" }}</td>
                  <td class="col_text">{{ item.row.remarks || "-" }}</td>
                  <td class="nowrap">{{ formatDatetime(item.row.updatedDatetime) }}</td>
                  <td class="nowrap">
                    <v-btn
                      size="small" variant="outlined" color="indigo-darken-1" class="mr-2"
                      :disabled="editingKey != null || isSaving"
                      @click="startEdit(item.row)"
                    >{{ $t('setting.action.edit') }}</v-btn>
                    <v-btn
                      size="small" variant="outlined" color="red-darken-2"
                      :disabled="editingKey != null || isSaving"
                      @click="confirmDelete(item.row)"
                    >{{ $t('common.action.delete') }}</v-btn>
                  </td>
                </template>
              </tr>
              <tr v-if="rows.length === 0">
                <td colspan="6" class="empty_note">{{ $t('setting.message.noCode') }}</td>
              </tr>
              <tr v-else-if="pagedRows.length === 0">
                <td colspan="6">{{ $t('common.message.noData') }}</td>
              </tr>
            </tbody>
          </v-table>

          <!-- 1ページの件数・ページャは他の画面と同じ（10/20/50/100/すべて、既定50件。CR-002 S-25） -->
          <v-row>
            <v-col cols="3"></v-col>
            <v-col cols="6">
              <v-pagination v-model="page" :length="pageCount" :total-visible="9" />
            </v-col>
            <v-col cols="3">
              <v-row>
                <v-spacer></v-spacer>
                <v-col cols="10" class="d-flex justify-end">
                  <v-select density="compact" variant="outlined" hide-details="auto" v-model="displayPerPage"
                    :items="displayPerPageItem" item-title="name" item-value="value"
                    :label="$t('common.label.displayCountPerPage')" return-object
                    @update:modelValue="page = 1" />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" class="text-right">
                  {{ displayRange.minNo }} - {{ displayRange.maxNo }}{{ $t('common.label.displayedDataRange') }} / {{ filteredRows.length }}{{ $t('common.label.totalCountOf') }}
                </v-col>
              </v-row>
            </v-col>
          </v-row>

        </v-container>

        <!-- 削除の確認。最後の1件を消すときは、他社の商品まで計上されるようになることも伝える -->
        <v-dialog :model-value="deleteTarget != null" width="520" @update:model-value="deleteTarget = null">
          <v-card v-if="deleteTarget">
            <v-card-title><h3><strong>{{ deleteIsLast ? $t('setting.label.zeroConfirmTitle') : $t('setting.label.deleteTitle') }}</strong></h3></v-card-title>
            <v-card-text style="white-space: pre-line">
              {{ deleteIsLast
                ? $t('setting.message.zeroConfirm')
                : $t('setting.message.deleteConfirm', { code: deleteTarget.companyCode, name: deleteTarget.companyName || '-' }) }}
            </v-card-text>
            <v-card-actions class="pa-4">
              <v-spacer></v-spacer>
              <v-btn text @click="deleteTarget = null">{{ $t('common.action.cancel') }}</v-btn>
              <v-btn color="red-darken-2" dark :loading="isSaving" @click="deleteRow">{{ $t('common.action.delete') }}</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <!-- 追加の確認。確定していない棚卸の数量が数え直しで増えるので、件数と追加するコードを見せる（RKJ-RFID-CHG-001 B） -->
        <v-dialog :model-value="addConfirm != null" width="560" persistent>
          <v-card v-if="addConfirm">
            <v-card-title><h3><strong>{{ $t('setting.label.addConfirmTitle') }}</strong></h3></v-card-title>
            <v-card-text>
              <p>{{ $t('setting.message.addConfirm', { count: addConfirm.count }) }}</p>
              <div class="mt-3 hint">{{ $t('setting.label.addedCodes') }}</div>
              <ul class="added_list">
                <li v-for="c in addConfirm.codes" :key="c.companyCode">
                  <span class="code_cell">{{ c.companyCode }}</span>（{{ c.companyName || "-" }}）
                </li>
              </ul>
              <p class="mt-3 hint">{{ $t('setting.message.addConfirmNote') }}</p>
            </v-card-text>
            <v-card-actions class="pa-4">
              <v-spacer></v-spacer>
              <v-btn text :disabled="isSaving" @click="addConfirm = null">{{ $t('common.action.cancel') }}</v-btn>
              <v-btn color="indigo-darken-1" variant="flat" :loading="isSaving" @click="confirmAdd">OK</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <CompanyCodeBulkDialog
          v-if="bulkDialogVisible"
          :handle-cancel="() => (bulkDialogVisible = false)"
          :handle-imported="onImported"
        />
      </v-main>
    </AppWrapperView>
  </v-app>
</template>

<script>
import AppWrapperView from "@/components/views/AppWrapperView";
import CompanyCodeBulkDialog from "@/components/dialog/CompanyCodeBulkDialog";
import api from "@/apis/staff";
import utils from "@/utils";

/**
 * 設定
 *
 * 企業コード（JAN13桁の先頭7桁）を本部が登録する画面。
 * 良品計画様システムからの連携は行わないため、ここが唯一の登録口になる。
 * リーダーは起動時・同期時にこの値を受け取り、自社製品かどうかの判定に使う。
 *
 * 一覧は読み取り専用で、行の「編集」を押した行だけを入力欄にする（2026-10-23）。
 * 「編集完了」「削除」はその場でサーバーに反映する。API は一覧全体を受け取って入れ替える
 * /config/company-code/save のままで、画面側で「その1行だけ変えた一覧」を作って送る。
 * 一度に編集できるのは1行だけ（他の行の操作は止める）。
 *
 * 棚卸中（確定していない棚卸がある間）も、企業コードの追加と名称・備考の修正はできる（RKJ-RFID-CHG-001）。
 * 追加すると確定していない棚卸の数量が増えるので、サーバーが confirmRequired を返したら確認ダイアログを出し、
 * OK なら confirmed を付けて送り直す。キャンセルなら保存しない（編集中の行はそのまま残す）。
 *
 * 件数の上限は設けない（数千件になりうる）。表示は1ページの件数（他の画面と同じ選択肢）で区切り、
 * 絞り込みで目的の行を探せるようにしている。
 */

// 行を識別するための連番。v-for の key に使う（行を消すと添字がずれるため、添字は使えない）
let keySeq = 0;
const withKey = (row) => ({ ...row, _key: ++keySeq });
export default {
  name: "SettingPage",
  components: { AppWrapperView, CompanyCodeBulkDialog },

  data() {
    return {
      // 1ページの表示件数（他の画面と同じ選択肢・既定50件。CR-002 S-25）
      displayPerPage: utils.getDisplayPerPage(this.$t),
      displayPerPageItem: utils.getDisplayPerPageItem(this.$t),
      rows: [],
      page: 1,
      // 入力中の絞り込みと、「検索」を押して反映した絞り込み
      filterText: "",
      appliedFilter: "",
      isSearching: false,
      bulkDialogVisible: false,
      // 編集中の行（_key）と、その入力中の値。キャンセルしたら捨てる
      editingKey: null,
      draft: null,
      // 削除の確認ダイアログに出す行
      deleteTarget: null,
      // 追加の確認ダイアログ（確定していない棚卸の件数・追加するコード・送り直す一覧）
      addConfirm: null,
      isSaving: false,
      errorMsg: "",
      savedMsg: "",
      // 一覧を読んだときの企業コード表の版数。保存のときに送り、ほかの人が先に変えていたら断ってもらう
      // （一覧を丸ごと送るので、そのまま保存すると相手の追加が消える。ソースレビュー C12）
      version: null,
    };
  },

  computed: {
    // 登録済みの件数（追加中でまだ保存していない行は数えない）
    savedCount() {
      return this.rows.filter(r => !r._new).length;
    },
    // 絞り込み後の行。全体での位置（index）も持たせる（No に使う）。編集中の行は絞り込みで消さない
    filteredRows() {
      const q = (this.appliedFilter || "").trim();
      const all = this.rows.map((row, index) => ({ row, index }));
      if (!q) return all;
      return all.filter(({ row }) =>
        this.isEditing(row) ||
        (row.companyCode || "").includes(q) ||
        (row.companyName || "").includes(q) ||
        (row.remarks || "").includes(q));
    },
    // 1ページの件数。「すべて」（0）は全件
    pageSize() {
      const v = this.displayPerPage?.value ?? 50;
      return v > 0 ? v : Math.max(1, this.filteredRows.length);
    },
    pageCount() {
      return Math.max(1, Math.ceil(this.filteredRows.length / this.pageSize));
    },
    pagedRows() {
      const start = (this.page - 1) * this.pageSize;
      return this.filteredRows.slice(start, start + this.pageSize);
    },
    // 「x - y件目」の表示
    displayRange() {
      const total = this.filteredRows.length;
      if (total === 0) return { minNo: 0, maxNo: 0 };
      const minNo = (this.page - 1) * this.pageSize + 1;
      return { minNo, maxNo: Math.min(total, this.page * this.pageSize) };
    },
    canFinish() {
      return this.draft != null && this.checkCode(this.draft.companyCode) === true && !this.isSaving;
    },
    deleteIsLast() {
      return this.deleteTarget != null && this.savedCount === 1;
    },
  },

  watch: {
    // 行を消して最後のページが無くなったら、残っている最後のページへ
    pageCount(n) {
      if (this.page > n) this.page = n;
    },
  },

  mounted() {
    this.search();
  },

  methods: {
    search() {
      this.isSearching = true;
      api.post("/config/company-code/search", {}).then((res) => {
        if (res.data.status !== "0") {
          this.errorMsg = res.data.errorMessage || this.$t("common.message.error");
          return;
        }
        this.setRows(res.data.results.companyCodeList);
        this.version = res.data.results.version ?? null;
        this.page = 1;
      }).finally(() => {
        this.isSearching = false;
      });
    },

    // 「検索」：一覧を取り直し、入力中の絞り込みを反映する
    onSearch() {
      if (this.editingKey != null) return;
      this.appliedFilter = this.filterText;
      this.search();
    },

    // 「クリア」：入力中の絞り込みを消す（反映は「検索」を押したとき。他の画面と同じ）
    clear() {
      this.filterText = "";
    },

    // 保存が断られたあと、エラーの表示は残したまま一覧と版数だけ取り直す
    reloadKeepingMessage() {
      api.post("/config/company-code/search", {}).then((res) => {
        if (res.data.status !== "0") return;
        this.setRows(res.data.results.companyCodeList);
        this.version = res.data.results.version ?? null;
      });
    },

    setRows(list) {
      this.rows = (list || []).map(withKey);
      this.editingKey = null;
      this.draft = null;
    },

    // 一括登録の結果を反映する。サーバーの最新の一覧で置き換える
    onImported(list, version) {
      this.setRows(list);
      this.version = version ?? null;
      this.filterText = "";
      this.appliedFilter = "";
      this.page = 1;
      this.errorMsg = "";
      this.savedMsg = "";
    },

    isEditing(row) {
      return this.editingKey != null && row._key === this.editingKey;
    },

    startEdit(row) {
      this.errorMsg = "";
      this.savedMsg = "";
      this.editingKey = row._key;
      this.draft = { companyCode: row.companyCode, companyName: row.companyName || "", remarks: row.remarks || "" };
    },

    // 編集前の値に戻す。追加中の行は消す
    cancelEdit() {
      this.rows = this.rows.filter(r => !(r._new && r._key === this.editingKey));
      this.editingKey = null;
      this.draft = null;
      this.errorMsg = "";
    },

    // 先頭に空の行を足して編集中にする。ボタンを一覧の上へ移したので、足した行もボタンのすぐ下（先頭のページ）に出す
    // （店舗登録の「新規登録」と同じ。S-39）。足した行が見えるよう絞り込みは外す
    addRow() {
      this.errorMsg = "";
      this.savedMsg = "";
      const row = withKey({ companyCode: "", companyName: "", remarks: "", updatedDatetime: null, _new: true });
      this.rows.unshift(row);
      this.editingKey = row._key;
      this.draft = { companyCode: "", companyName: "", remarks: "" };
      this.filterText = "";
      this.appliedFilter = "";
      this.page = 1;
    },

    checkCode(v) {
      if (!/^[0-9]{7}$/.test(v || "")) return this.$t("setting.validation.companyCode");
      if (this.rows.some(r => r._key !== this.editingKey && !r._new && r.companyCode === v)) {
        return this.$t("setting.validation.duplicate");
      }
      return true;
    },

    formatDatetime(value) {
      return value ? utils.putSplitWordToCalendarWithTime(value) : "-";
    },

    // 登録済みの行（追加中の行は除く）を、送る形にする
    toList(rows) {
      return rows.filter(r => !r._new).map(({ companyCode, companyName, remarks }) => ({ companyCode, companyName, remarks }));
    },

    // 一覧全体を送って入れ替える。成功したらサーバーの最新の一覧で置き換える。
    // 追加で確定していない棚卸の数量が増えるときは、保存されずに confirmRequired が返るので確認ダイアログを出す
    // （OK で confirmAdd が confirmed=true で送り直す）。そのときは false を返す
    save(list, okMessage, confirmed = false) {
      this.errorMsg = "";
      this.savedMsg = "";
      this.isSaving = true;
      return api.post("/config/company-code/save", { companyCodeList: list, confirmed, expectedVersion: this.version })
        .then((res) => {
          if (res.data.status !== "0") {
            this.errorMsg = res.data.errorMessage || this.$t("common.message.error");
            // ほかの人が先に変えていたときは、最新の一覧（と版数）を読み直す。メッセージは残す
            if ((res.data.errorList ?? []).some(e => e.field === "expectedVersion")) this.reloadKeepingMessage();
            return false;
          }
          const results = res.data.results;
          if (results.confirmRequired) {
            const added = results.addedCompanyCodes || [];
            this.addConfirm = {
              count: results.openInventoryCount,
              codes: added.map(code => list.find(r => r.companyCode === code) || { companyCode: code }),
              list,
              okMessage,
            };
            return false;
          }
          this.setRows(results.companyCodeList);
          this.version = results.version ?? null;
          this.savedMsg = results.promotedQuantity > 0
            ? okMessage + this.$t("setting.message.promoted",
              { inventories: results.promotedInventoryCount, quantity: results.promotedQuantity })
            : okMessage;
          return true;
        })
        .catch(() => {
          this.errorMsg = this.$t("common.message.unknownError");
          return false;
        })
        .finally(() => {
          this.isSaving = false;
        });
    },

    // 編集中の1行だけを入れ替えた一覧を送る（追加中の行なら末尾に足す）
    finishEdit() {
      if (!this.canFinish) return;
      const edited = {
        companyCode: this.draft.companyCode,
        companyName: (this.draft.companyName || "").trim(),
        remarks: (this.draft.remarks || "").trim(),
      };
      const editingRow = this.rows.find(r => r._key === this.editingKey);
      const list = editingRow && editingRow._new
        ? [...this.toList(this.rows), edited]
        : this.rows.filter(r => !r._new).map(r => (r._key === this.editingKey ? edited : this.toList([r])[0]));
      this.save(list, this.$t("setting.message.saved")).then((ok) => {
        if (ok) this.showRow(edited.companyCode);
      });
    },

    // 追加の確認で OK。同じ一覧を confirmed=true で送り直す
    confirmAdd() {
      const { list, okMessage, codes } = this.addConfirm;
      this.save(list, okMessage, true).then((ok) => {
        this.addConfirm = null;
        if (ok && codes.length > 0) this.showRow(codes[0].companyCode);
      });
    },

    // 保存したあとも、編集した行が見えるようにその企業コードの行のページへ
    showRow(companyCode) {
      const idx = this.rows.findIndex(r => r.companyCode === companyCode);
      if (idx >= 0 && !this.appliedFilter) this.page = Math.floor(idx / this.pageSize) + 1;
    },

    confirmDelete(row) {
      this.errorMsg = "";
      this.savedMsg = "";
      this.deleteTarget = row;
    },

    deleteRow() {
      const target = this.deleteTarget;
      const list = this.toList(this.rows.filter(r => r._key !== target._key));
      this.save(list, this.$t("setting.message.deleted", { code: target.companyCode })).then(() => {
        this.deleteTarget = null;
      });
    },
  },
};
</script>

<style scoped>
.section_title {
  font-size: 16px;
  font-weight: bold;
  border-left: 4px solid #3949ab;
  padding-left: 8px;
  margin-bottom: 6px;
}
.section_desc {
  font-size: 13px;
  color: #5b6472;
  margin: 0 0 14px;
}
.section_table :deep(thead th) {
  background-color: #3949ab !important;
  color: #ffffff !important;
  font-size: 14px !important;
  font-weight: 700 !important;
  height: 42px !important;
}
.section_table :deep(td) {
  font-size: 14px !important;
  height: 56px !important;
}
.editing_row {
  background-color: #f3f5fc;
}
/* 企業コードの欄。入力欄の下に出る誤りの文言が途中で切れないよう、幅を取り、文言は折り返す */
.code_col {
  min-width: 200px;
}
.code_input {
  max-width: 220px;
}
.code_input :deep(input),
.code_cell {
  font-family: "Consolas", "Courier New", monospace;
  letter-spacing: 0.08em;
}
.code_input :deep(.v-messages__message) {
  white-space: normal;
  line-height: 1.3;
}
/* 名称・備考は残りの幅を分け合い、長い文字は折り返す */
.col_text {
  width: 40%;
  min-width: 140px;
  word-break: break-all;
}
.nowrap {
  white-space: nowrap;
}
.hint {
  font-size: 12px;
  color: #5b6472;
}
.added_list {
  margin: 4px 0 0 20px;
}
.empty_note {
  color: #c62828;
  font-weight: 700;
}
</style>
